import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, mergeMap, Observable, take, throwError } from 'rxjs';
import { LoggerService } from '../services/logger.service';
import { AUTH_SESSION } from '@shared/di/auth-session.token';

let isRefreshing: boolean = false;
const refreshed: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export function httpErrorInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const logger = inject(LoggerService);
  const session = inject(AUTH_SESSION);

  function addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  function resetRefreshingState() {
    isRefreshing = false;
    refreshed.next(null);
  }

  return session.rememberMe$.pipe(
    take(1),
    mergeMap((rememberMe: boolean) => {
      return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
          logger.info('HttpErrorInterceptor', `Received error with status: ${error.status}`);
          // Handle 401 errors -----------------------------------------------------------------------------------------
          // - refresh token endpoint
          if (error.status === 401 && request.url.includes('/auth/token')) {
            resetRefreshingState();
            session.logout(true);
            return throwError(() => new Error($localize`Impossibile rinnovare il token di accesso`));
          }
          // - others
          if (error.status === 401 && !request.url.includes('/auth/login')) {
            logger.info('HttpErrorInterceptor', 'Handling 401 error, isRefreshing:', isRefreshing);
            // handle the
            if (!isRefreshing) {
              isRefreshing = true;
              refreshed.next(null);
              logger.info('HttpErrorInterceptor', 'Starting token refresh');
              return session.refreshToken().pipe(
                mergeMap((response: any) => {
                  logger.info('HttpErrorInterceptor', 'Refresh token call succeeded');
                  isRefreshing = false;
                  if (!response || !response.accessToken) {
                    logger.error('HttpErrorInterceptor', 'Invalid refresh response', response);
                    resetRefreshingState();
                    return throwError(() => new Error($localize`Impossibile rinnovare il token di accesso`));
                  }
                  session.storeRefreshedTokens(response.accessToken, response.refreshToken);
                  refreshed.next(response.accessToken);
                  if (!rememberMe) {
                    session.logout(true);
                    return throwError(() => new Error($localize`Autenticazione scaduta`));
                  }
                  logger.info('HttpErrorInterceptor', 'Retrying original request with new token');
                  return next(addTokenToRequest(request, response.accessToken));
                }),
                catchError((refreshError) => {
                  logger.error('HttpErrorInterceptor', 'Error in refresh token flow', refreshError);
                  resetRefreshingState();
                  session.logout(true);
                  return throwError(() => new Error($localize`Impossibile aggiornare lo stato di autenticazione`));
                })
              );
            }
            if (isRefreshing) {
              logger.info('HttpErrorInterceptor', 'Waiting for ongoing refresh to complete');
              return refreshed.pipe(
                filter((token: string) => token !== null),
                take(1),
                mergeMap((token: string) => {
                  if (!token) {
                    // If we get here with no token, the refresh must have failed
                    return throwError(() => new Error($localize`Impossibile rinnovare il token di accesso`));
                  }
                  logger.info('HttpErrorInterceptor', 'Using token from ongoing refresh');
                  return next(addTokenToRequest(request, token));
                }),
                catchError((error) => {
                  logger.error('HttpErrorInterceptor', 'Error while waiting for refresh', error);
                  return throwError(() => error);
                })
              );
            }
          }
          // -----------------------------------------------------------------------------------------------------------
          return throwError(() => error);
        })
      );
    })
  );
}
